import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { closePlansApi, ClosePlan } from '../api/closePlans';
import { leadsApi } from '../api/leads';
import { accountsApi } from '../api/accounts';
import PremiumLayout from '../components/PremiumLayout';
import { PlusIcon, PencilIcon, TrashIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

const ClosePlans: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<ClosePlan | null>(null);
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    lead_id: '',
    pain_points: '',
    decision_criteria: '',
    objections: '',
    next_steps: '',
  });

  const queryClient = useQueryClient();

  const { data: leads = [] } = useQuery({
    queryKey: ['leads'],
    queryFn: leadsApi.getAll,
  });

  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts'],
    queryFn: accountsApi.getAll,
  });

  const { data: closePlans = [], isLoading } = useQuery({
    queryKey: ['close-plans', selectedLeadId],
    queryFn: () => selectedLeadId ? closePlansApi.getAllByLeadId(selectedLeadId) : Promise.resolve([]),
    enabled: !!selectedLeadId,
  });

  const createMutation = useMutation({
    mutationFn: closePlansApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['close-plans'] });
      setShowModal(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<ClosePlan> }) =>
      closePlansApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['close-plans'] });
      setShowModal(false);
      setEditingPlan(null);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: closePlansApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['close-plans'] });
    },
  });

  const resetForm = () => {
    setFormData({
      lead_id: '',
      pain_points: '',
      decision_criteria: '',
      objections: '',
      next_steps: '',
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate that lead is selected
    if (!formData.lead_id || formData.lead_id === '') {
      alert('Please select a lead');
      return;
    }

    // Validate that at least one field has content (not just whitespace)
    const hasContent = [
      formData.pain_points?.trim(),
      formData.decision_criteria?.trim(),
      formData.objections?.trim(),
      formData.next_steps?.trim(),
    ].some(field => field && field.length > 0);

    if (!hasContent) {
      alert('At least one field (Pain Points, Decision Criteria, Objections, or Next Steps) must be filled');
      return;
    }

    const planData = {
      ...formData,
      lead_id: parseInt(formData.lead_id),
    };

    if (editingPlan) {
      updateMutation.mutate({ id: editingPlan.id, data: planData });
    } else {
      createMutation.mutate(planData);
    }
  };

  const handleEdit = (plan: ClosePlan) => {
    setEditingPlan(plan);
    setFormData({
      lead_id: plan.lead_id.toString(),
      pain_points: plan.pain_points || '',
      decision_criteria: plan.decision_criteria || '',
      objections: plan.objections || '',
      next_steps: plan.next_steps || '',
    });
    setShowModal(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this close plan?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleLeadSelect = (leadId: number) => {
    setSelectedLeadId(leadId);
  };

  if (isLoading) {
    return (
      <PremiumLayout title="Close Plans">
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600"></div>
        </div>
      </PremiumLayout>
    );
  }

  return (
    <PremiumLayout
      title="Close Plans"
      subtitle="Manage close plans for your leads"
    >
      <div className="space-y-6">
        {/* Lead Selector */}
        <div className="premium-card p-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Select Lead
          </label>
          <select
            value={selectedLeadId || ''}
            onChange={(e) => handleLeadSelect(parseInt(e.target.value))}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">-- Select a lead --</option>
            {leads.map((lead) => {
              // Get account name if available (company/account name is priority)
              const account = accounts.find(a => a.id === lead.account_id);
              const companyName = account?.name || lead.project_name || lead.brand || lead.name;
              const contactName = lead.first_name && lead.last_name
                ? `${lead.first_name} ${lead.last_name}`
                : lead.first_name || lead.lead_name || '';
              return (
                <option key={lead.id} value={lead.id}>
                  {companyName} {contactName ? `(${contactName})` : ''} - {lead.stage}
                </option>
              );
            })}
          </select>
        </div>

        {/* Actions */}
        {selectedLeadId && (
          <div className="flex justify-end">
            <button
              onClick={() => {
                setFormData({ ...formData, lead_id: selectedLeadId.toString() });
                setShowModal(true);
              }}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create Close Plan
            </button>
          </div>
        )}

        {/* Close Plans List */}
        {selectedLeadId && (
          <div className="premium-card p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Close Plans</h2>
            {closePlans.length === 0 ? (
              <div className="text-center py-12">
                <DocumentTextIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No close plans found for this lead</p>
              </div>
            ) : (
              <div className="space-y-4">
                {closePlans.map((plan) => (
                  <div key={plan.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="font-semibold text-gray-900">Close Plan #{plan.id}</h3>
                      <div className="space-x-2">
                        <button
                          onClick={() => handleEdit(plan)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(plan.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="font-semibold text-gray-700">Pain Points:</p>
                        <p className="text-gray-600">{plan.pain_points || '—'}</p>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-700">Decision Criteria:</p>
                        <p className="text-gray-600">{plan.decision_criteria || '—'}</p>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-700">Objections:</p>
                        <p className="text-gray-600">{plan.objections || '—'}</p>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-700">Next Steps:</p>
                        <p className="text-gray-600">{plan.next_steps || '—'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center z-50 pt-4 pb-4 px-4">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {editingPlan ? 'Edit Close Plan' : 'Create Close Plan'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Lead
                  </label>
                  <select
                    value={formData.lead_id}
                    onChange={(e) => setFormData({ ...formData, lead_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    <option value="">-- Select a lead --</option>
                    {leads.map((lead) => {
                      const contactName = lead.first_name && lead.last_name
                        ? `${lead.first_name} ${lead.last_name}`
                        : lead.first_name || lead.lead_name || 'No Contact';
                      const brandName = lead.brand || lead.name || 'No Brand';
                      return (
                        <option key={lead.id} value={lead.id}>
                          {contactName} - {brandName}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Pain Points
                  </label>
                  <textarea
                    value={formData.pain_points}
                    onChange={(e) => setFormData({ ...formData, pain_points: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Decision Criteria
                  </label>
                  <textarea
                    value={formData.decision_criteria}
                    onChange={(e) => setFormData({ ...formData, decision_criteria: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Objections
                  </label>
                  <textarea
                    value={formData.objections}
                    onChange={(e) => setFormData({ ...formData, objections: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Next Steps
                  </label>
                  <textarea
                    value={formData.next_steps}
                    onChange={(e) => setFormData({ ...formData, next_steps: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    rows={3}
                  />
                </div>
                <div className="flex space-x-4">
                  <button
                    type="submit"
                    className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700"
                  >
                    {editingPlan ? 'Update' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingPlan(null);
                      resetForm();
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </PremiumLayout>
  );
};

export default ClosePlans;
